import React, { useEffect, useState } from 'react';
import { X, Bell, User } from 'lucide-react';

interface NotificationToastProps {
  notification: {
    id: string;
    title: string;
    message: string;
    leadId?: string;
    lead?: {
      id: string;
      firstName?: string;
      lastName?: string;
    };
    createdAt: string;
  };
  onDismiss: (notificationId: string) => void;
  onViewLead: (leadId: string) => void;
  autoHideDelay?: number;
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onDismiss,
  onViewLead,
  autoHideDelay = 10000, // 10 seconds
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [timeLeft, setTimeLeft] = useState(Math.ceil(autoHideDelay / 1000));

  useEffect(() => {
    if (autoHideDelay > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsVisible(false);
            setTimeout(() => onDismiss(notification.id), 300); // Allow fade out
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [autoHideDelay, notification.id, onDismiss]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => onDismiss(notification.id), 300);
  };

  const handleViewLead = () => {
    if (notification.leadId) {
      onViewLead(notification.leadId);
      handleDismiss();
    }
  };

  if (!isVisible) return null;

  const leadName = notification.lead 
    ? `${notification.lead.firstName || ''} ${notification.lead.lastName || ''}`.trim() || 'Lead'
    : 'Lead';

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm w-full bg-white rounded-lg shadow-lg border border-gray-200 transform transition-all duration-300 ease-in-out">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Bell className="w-4 h-4 text-blue-600" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{notification.title}</p>
              <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
              {notification.leadId && (
                <div className="flex items-center mt-2 text-xs text-gray-500">
                  <User className="w-3 h-3 mr-1" />
                  <span>{leadName}</span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {notification.leadId && (
          <div className="mt-3 flex items-center justify-between">
            <button
              onClick={handleViewLead}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              View Lead
            </button>
            {autoHideDelay > 0 && (
              <div className="text-xs text-gray-400">
                Auto-dismiss in {timeLeft}s
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationToast;
